/**
 * Utility for managing cross-tab locks using localStorage with TTL (Time To Live)
 * Prevents multiple tabs from performing the same action simultaneously
 */

import { LocalStorageService } from "~Shared/services/localstorage.service"
import { delay } from "~Shared/utils/helper.utils"

interface LockItem {
    value: boolean
    expiry: number
}

const DEFAULT_TTL = 5000 // 5 seconds in milliseconds
const RELEASE_DELAY_MS = DEFAULT_TTL

/**
 * Attempts to acquire a lock with expiry time
 * @param key - The lock key identifier
 * @param ttl - Time to live in milliseconds (default: 5000ms)
 * @returns true if lock was acquired, false if lock already exists
 */
const acquireLock = (key: string, ttl: number = DEFAULT_TTL): boolean => {
    const existingLock = getLock(key)
    // If lock already exists and hasn't expired, return false
    if (existingLock) {
        return false
    }

    // Acquire the lock
    const now = new Date()
    const item: LockItem = {
        value: true,
        expiry: now.getTime() + ttl,
    }

    LocalStorageService.set(key, JSON.stringify(item))
    return true
}

const releaseLock = (key: string): void => {
    LocalStorageService.remove(key)
}

const getLock = (key: string): boolean => {
    const itemStr = LocalStorageService.get(key)
    // If the item doesn't exist, return false
    if (!itemStr) {
        return false
    }

    try {
        const item: LockItem = JSON.parse(itemStr)
        const now = new Date()
        // Compare the expiry time of the item with the current time
        if (now.getTime() > item.expiry) {
            // If the item is expired, delete the item from storage and return false
            LocalStorageService.remove(key)
            return false
        }

        return item.value
    } catch {
        // If parsing fails, remove the corrupted item
        LocalStorageService.remove(key)
        return false
    }
}

const randomizedDelay = async (min: number = 0, max: number = 1000): Promise<void> => {
    const time = Math.floor(Math.random() * (max - min + 1)) + min
    await delay(time)
}

/**
 * Executes a callback function if lock can be acquired
 * This prevent multiple tabs executing the same action simultaneously
 * @param key - The lock key identifier
 * @param callback - Function to execute if lock is acquired
 * @param ttl - Time to live in milliseconds (default: 5000ms)
 * @returns true if callback was executed, false if lock couldn't be acquired
 */
export const executeWithCrossTabLock = async (key: string, callback: () => void, ttl: number = DEFAULT_TTL): Promise<boolean> => {
    await randomizedDelay()
    if (acquireLock(key, ttl)) {
        try {
            callback()
            return true
        } finally {
            setTimeout(() => releaseLock(key), RELEASE_DELAY_MS)
        }
    }
    return false
}
